import { useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { ChevronRight, LayoutGrid, List, Package, Plus, Rocket, Settings, UploadCloud, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type * as S from "@shipyard/shared";
import { CommitPicker } from "@/components/commit-picker";
import { BuildsTable } from "@/components/builds";
import { PlatformIcon, toastError } from "@/components/common";
import { JointReleaseDialog } from "@/components/joint-release";
import { KeystoreCard } from "@/components/keystore";
import { PublishButton } from "@/components/publish";
import { EditTagsButton, TagBadge, TagList } from "@/components/tags";
import { Badge, Button, Card, EmptyState, ErrorBox, Field, Input, PageHeader, Spinner, Switch, Table, Td, Textarea, Th } from "@/components/ui";
import { http, uploadBuild } from "@/lib/api";
import { useApp, useApps, useBuilds, useMe, useRecentBuilds } from "@/lib/queries";
import { isOrgAdmin } from "@/lib/roles";
import { cn, formatBytes } from "@/lib/utils";

const VIEW_KEY = "shipyard.appsView";
type AppsView = "grid" | "list";

function useAppsView() {
  const [view, setView] = useState<AppsView>(() => {
    try {
      return localStorage.getItem(VIEW_KEY) === "list" ? "list" : "grid";
    } catch {
      return "grid";
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(VIEW_KEY, view);
    } catch {
      // ignore
    }
  }, [view]);
  return [view, setView] as const;
}

function PlatformIds({ app }: { app: S.App }) {
  return (
    <>
      {app.iosBundleId && (
        <div className="flex min-w-0 items-center gap-1.5">
          <PlatformIcon platform="ios" className="h-3.5 w-3.5 shrink-0" />
          <span className="truncate font-mono">{app.iosBundleId}</span>
        </div>
      )}
      {app.androidPackage && (
        <div className="flex min-w-0 items-center gap-1.5">
          <PlatformIcon platform="android" className="h-3.5 w-3.5 shrink-0" />
          <span className="truncate font-mono">{app.androidPackage}</span>
        </div>
      )}
    </>
  );
}

function AppCard({ app }: { app: S.App }) {
  return (
    <Link to="/apps/$appId" params={{ appId: app.id }}>
      <Card className="group h-full p-5 transition-colors hover:border-brand-500">
        <div className="flex items-start justify-between gap-2">
          <h2 className="font-semibold">{app.name}</h2>
          <ChevronRight className="h-4 w-4 text-fg-muted transition-transform group-hover:translate-x-0.5" />
        </div>
        <TagList tags={app.tags} className="mt-2" />
        <div className="mt-3 space-y-1.5 text-xs text-fg-muted">
          <PlatformIds app={app} />
        </div>
        <div className="mt-4 flex flex-wrap gap-1.5">
          {app.locales.map((l) => (
            <Badge key={l}>{l}</Badge>
          ))}
        </div>
      </Card>
    </Link>
  );
}

function AppsTable({ apps }: { apps: S.App[] }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <Card>
      <Table>
        <thead>
          <tr>
            <Th>{t("common.name")}</Th>
            <Th className="hidden sm:table-cell">{t("apps.identifiers")}</Th>
            <Th className="hidden lg:table-cell">{t("appSettings.locales")}</Th>
            <Th />
          </tr>
        </thead>
        <tbody>
          {apps.map((app) => (
            <tr key={app.id} className="cursor-pointer hover:bg-muted/50" onClick={() => void navigate({ to: "/apps/$appId", params: { appId: app.id } })}>
              <Td>
                <Link to="/apps/$appId" params={{ appId: app.id }} className="font-medium" onClick={(e) => e.stopPropagation()}>
                  {app.name}
                </Link>
                <TagList tags={app.tags} className="mt-1" />
              </Td>
              <Td className="hidden max-w-xs space-y-1 text-xs text-fg-muted sm:table-cell">
                <PlatformIds app={app} />
              </Td>
              <Td className="hidden text-xs text-fg-muted lg:table-cell">{app.locales.join(", ")}</Td>
              <Td className="text-right">
                <ChevronRight className="ml-auto h-4 w-4 text-fg-muted" />
              </Td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Card>
  );
}

function ViewToggle({ view, onChange }: { view: AppsView; onChange: (v: AppsView) => void }) {
  const { t } = useTranslation();
  const options: [AppsView, typeof LayoutGrid, string][] = [
    ["grid", LayoutGrid, t("apps.viewGrid")],
    ["list", List, t("apps.viewList")],
  ];
  return (
    <div className="inline-flex rounded-lg bg-muted p-1">
      {options.map(([value, Icon, label]) => (
        <button
          key={value}
          onClick={() => onChange(value)}
          title={label}
          aria-label={label}
          aria-pressed={view === value}
          className={cn("rounded-md px-2 py-1", view === value ? "bg-panel shadow-sm" : "text-fg-muted hover:text-fg")}
        >
          <Icon className="h-4 w-4" />
        </button>
      ))}
    </div>
  );
}

function RecentBuilds({ apps }: { apps: S.App[] }) {
  const { t } = useTranslation();
  const builds = useRecentBuilds(10);
  const appNames = useMemo(() => new Map(apps.map((a) => [a.id, a.name])), [apps]);
  return (
    <Card>
      <div className="border-b px-5 py-3">
        <h2 className="font-semibold">{t("apps.recentBuilds")}</h2>
      </div>
      {builds.isLoading ? (
        <div className="p-5">
          <Spinner />
        </div>
      ) : builds.error ? (
        <div className="p-5">
          <ErrorBox error={builds.error} />
        </div>
      ) : !builds.data?.length ? (
        <p className="px-5 py-10 text-center text-sm text-fg-muted">{t("apps.noRecentBuilds")}</p>
      ) : (
        <BuildsTable builds={builds.data} appNames={appNames} />
      )}
    </Card>
  );
}

export function AppsPage() {
  const { t } = useTranslation();
  const me = useMe();
  const apps = useApps();
  const navigate = useNavigate();
  const { tag: tagFilter } = useSearch({ strict: false }) as { tag?: string };
  const [view, setView] = useAppsView();
  const isAdmin = isOrgAdmin(me.data);

  const usedTags = useMemo(() => {
    const byId = new Map<string, S.Tag>();
    for (const app of apps.data ?? []) for (const tag of app.tags) byId.set(tag.id, tag);
    return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [apps.data]);
  const visible = tagFilter ? (apps.data ?? []).filter((a) => a.tags.some((tag) => tag.id === tagFilter)) : (apps.data ?? []);
  const setTag = (tag: string | undefined) => void navigate({ to: "/apps", search: tag ? { tag } : {} });

  return (
    <>
      <PageHeader
        title={t("apps.title")}
        actions={
          <>
            {!!apps.data?.length && <ViewToggle view={view} onChange={setView} />}
            {isAdmin && (
              <Link to="/apps/new">
                <Button>
                  <Plus className="h-4 w-4" />
                  {t("apps.new")}
                </Button>
              </Link>
            )}
          </>
        }
      />
      {apps.isLoading ? (
        <Spinner />
      ) : apps.error ? (
        <ErrorBox error={apps.error} />
      ) : !apps.data?.length ? (
        <EmptyState icon={<Package className="h-8 w-8" />} title={t("apps.empty")}>
          {!isAdmin && t("apps.emptyUser")}
        </EmptyState>
      ) : (
        <div className="space-y-8">
          <div className="space-y-4">
            {usedTags.length > 0 && (
              <div className="flex flex-wrap items-center gap-2">
                {usedTags.map((tag) => {
                  const on = tag.id === tagFilter;
                  return (
                    <button
                      key={tag.id}
                      onClick={() => setTag(on ? undefined : tag.id)}
                      aria-pressed={on}
                      className={cn("rounded-full transition-opacity", tagFilter && !on && "opacity-40 hover:opacity-80")}
                    >
                      <TagBadge tag={tag} className={cn(on && "ring-2 ring-brand-500 ring-offset-2 ring-offset-bg")} />
                    </button>
                  );
                })}
                {tagFilter && (
                  <button onClick={() => setTag(undefined)} className="flex items-center gap-1 text-xs text-fg-muted hover:text-fg">
                    <X className="h-3.5 w-3.5" />
                    {t("apps.clearFilter")}
                  </button>
                )}
              </div>
            )}
            {!visible.length ? (
              <EmptyState title={t("apps.noMatch")} />
            ) : view === "list" ? (
              <AppsTable apps={visible} />
            ) : (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {visible.map((app) => (
                  <AppCard key={app.id} app={app} />
                ))}
              </div>
            )}
          </div>
          <RecentBuilds apps={apps.data} />
        </div>
      )}
    </>
  );
}

// ------------------------------------------------------------------ Upload

interface UploadState {
  file: File;
  progress: number;
  abort: () => void;
}

function Dropzone({ app }: { app: S.App }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [upload, setUpload] = useState<UploadState | null>(null);
  const [showOptions, setShowOptions] = useState(false);
  const [commit, setCommit] = useState("");
  const [notes, setNotes] = useState("");
  const [autoNotes, setAutoNotes] = useState(true);

  const start = useCallback(
    (file: File) => {
      const lower = file.name.toLowerCase();
      if (!lower.endsWith(".ipa") && !lower.endsWith(".aab")) {
        toast.error(t("upload.invalidType"));
        return;
      }
      const { promise, abort } = uploadBuild(app.id, file, { commit, notes, autoNotes: String(autoNotes) }, (progress) =>
        setUpload((u) => (u ? { ...u, progress } : u)),
      );
      setUpload({ file, progress: 0, abort });
      promise
        .then((build) => {
          toast.success(t("upload.done"));
          setCommit("");
          setNotes("");
          void qc.invalidateQueries({ queryKey: ["apps", app.id, "builds"] });
          void navigate({ to: "/apps/$appId/builds/$buildId", params: { appId: app.id, buildId: build.id } });
        })
        .catch((err) => {
          if (err?.code !== "aborted") toastError(err);
        })
        .finally(() => setUpload(null));
    },
    [app.id, autoNotes, commit, navigate, notes, qc, t],
  );

  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file && !upload) start(file);
  };

  return (
    <Card className="p-4">
      {upload ? (
        <div className="space-y-3 p-2">
          <div className="flex items-center justify-between gap-3 text-sm">
            <span className="truncate font-medium">{t("upload.uploading", { name: upload.file.name })}</span>
            <span className="flex items-center gap-2 text-fg-muted">
              {Math.round(upload.progress * 100)}% · {formatBytes(upload.file.size)}
              <Button size="icon" variant="ghost" onClick={upload.abort} aria-label={t("common.cancel")}>
                <X className="h-4 w-4" />
              </Button>
            </span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-muted">
            <div className="h-full bg-brand-600 transition-[width]" style={{ width: `${upload.progress * 100}%` }} />
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          className={cn(
            "flex w-full flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed px-6 py-8 text-center transition-colors",
            dragging ? "border-brand-500 bg-brand-50 dark:bg-sky-950/30" : "hover:border-brand-500/60 hover:bg-muted/50",
          )}
        >
          <UploadCloud className="h-8 w-8 text-brand-600" />
          <span className="text-sm font-medium">{t("upload.drop")}</span>
          <span className="text-xs text-fg-muted">{t("upload.dropHint")}</span>
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept=".ipa,.aab"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) start(file);
          e.target.value = "";
        }}
      />
      {!upload && (
        <div className="mt-3">
          <button className="text-xs font-medium text-fg-muted hover:text-fg" onClick={() => setShowOptions((s) => !s)}>
            {showOptions ? "▾" : "▸"} {t("upload.options")}
          </button>
          {showOptions && (
            <div className="mt-3 grid gap-4 md:grid-cols-2">
              <Field label={t("upload.commit")} help={t("upload.commitHelp")} htmlFor="upload-sha" className="md:col-span-2">
                <CommitPicker appId={app.id} hasGit={!!app.gitProviderId && !!app.gitRepo} value={commit} onChange={setCommit} autoSelectLatest={false} idPrefix="upload" />
              </Field>
              {app.changelogMode !== "manual" && (
                <div className="flex items-center gap-2 md:col-span-2">
                  <Switch id="autoNotes" checked={autoNotes} onCheckedChange={setAutoNotes} />
                  <label htmlFor="autoNotes" className="text-sm">
                    {t(app.changelogMode === "auto" ? "upload.prefillAuto" : "upload.prefillCopy")}
                  </label>
                </div>
              )}
              <Field label={t("upload.notes")} className="md:col-span-2" htmlFor="notes">
                <Textarea id="notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={t("upload.notesPlaceholder")} rows={2} />
              </Field>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

// ------------------------------------------------------------------ App detail

export function AppPage({ appId }: { appId: string }) {
  const { t, i18n } = useTranslation();
  const app = useApp(appId);
  const me = useMe();
  const platforms: S.Platform[] = [];
  if (app.data?.iosBundleId) platforms.push("ios");
  if (app.data?.androidPackage) platforms.push("android");
  const [platform, setPlatform] = useState<S.Platform | undefined>(undefined);
  const builds = useBuilds(appId, platform);
  const [releaseOpen, setReleaseOpen] = useState(false);
  const qc = useQueryClient();

  if (app.isLoading) return <Spinner />;
  if (app.error || !app.data) return <ErrorBox error={app.error ?? new Error("Not found")} />;
  const canUpload = app.data.myRole === "manager";
  const togglePublic = async (b: S.Build) => {
    try {
      await http.put(`/builds/${b.id}/public`, { public: !b.public });
      void qc.invalidateQueries({ queryKey: ["apps", appId, "builds"] });
      // Listed anyway: the admin can add the certificate later.
      if (!b.public && b.platform === "ios" && !app.data!.adhocSigning) {
        toast.warning(t("publish.iosNotInstallable"), { description: t(app.data!.appleCredentialId ? "publish.noCertificate" : "publish.noAppleKey"), duration: 10_000 });
      }
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <>
      <PageHeader
        title={app.data.name}
        description={
          <span className="flex flex-col gap-1.5">
            <span className="flex flex-wrap gap-x-4 gap-y-1 font-mono text-xs">
              {app.data.iosBundleId && <span>{app.data.iosBundleId}</span>}
              {app.data.androidPackage && <span>{app.data.androidPackage}</span>}
            </span>
            <TagList tags={app.data.tags} />
          </span>
        }
        actions={
          <>
            {canUpload && <EditTagsButton app={app.data} />}
            {canUpload && <PublishButton app={app.data} />}
            {canUpload && app.data.iosBundleId && app.data.androidPackage && (
              <Button onClick={() => setReleaseOpen(true)}>
                <Rocket className="h-4 w-4" />
                {t("release.open")}
              </Button>
            )}
            {isOrgAdmin(me.data) && (
              <Link to="/apps/$appId/settings" params={{ appId }}>
                <Button variant="outline">
                  <Settings className="h-4 w-4" />
                  {t("apps.settings")}
                </Button>
              </Link>
            )}
          </>
        }
      />
      <div className="space-y-6">
        {canUpload && <Dropzone app={app.data} />}
        <Card>
          <div className="flex flex-wrap items-center justify-between gap-3 border-b px-5 py-3">
            <h2 className="font-semibold">{t("apps.builds")}</h2>
            {platforms.length > 1 && (
              <div className="inline-flex rounded-lg bg-muted p-1 text-sm">
                {[undefined, ...platforms].map((p) => (
                  <button
                    key={p ?? "all"}
                    onClick={() => setPlatform(p)}
                    className={cn("flex items-center gap-1.5 rounded-md px-3 py-1", platform === p ? "bg-panel shadow-sm" : "text-fg-muted")}
                  >
                    {p ? <PlatformIcon platform={p} className="h-3.5 w-3.5" /> : null}
                    {p ? (p === "ios" ? "iOS" : "Android") : t("apps.all")}
                  </button>
                ))}
              </div>
            )}
          </div>
          {builds.isLoading ? (
            <div className="p-5">
              <Spinner />
            </div>
          ) : !builds.data?.length ? (
            <p className="px-5 py-10 text-center text-sm text-fg-muted">{t("apps.noBuilds")}</p>
          ) : (
            <BuildsTable builds={builds.data} onTogglePublic={canUpload ? togglePublic : undefined} />
          )}
        </Card>
        {app.data.androidPackage && canUpload && <KeystoreCard appId={appId} canManage />}
      </div>
      {app.data.iosBundleId && app.data.androidPackage && <JointReleaseDialog app={app.data} open={releaseOpen} onOpenChange={setReleaseOpen} />}
    </>
  );
}
